// TypeIDs: `prefix_` + base32 UUIDv7 on the wire, `uuid` in storage
// (spec/03-architecture.md §3 and §7, spec/08-api-design.md §2.3).
//
// One id is readable in a log and in a support call (`ses_01j9…`), sorts by creation time, and
// cannot be passed where a different kind of id was meant, because each prefix has its own branded
// type. The codec here is the whole of the TypeID specification; nothing else in the repository may
// build or take apart an id by hand.
/** TypeID's base32 alphabet: Crockford's, lower case, without `i`, `l`, `o` and `u`. */
const ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';
/** 128 bits of UUID encoded five bits at a time. */
const SUFFIX_LENGTH = 26;
const SUFFIX_PATTERN = /^[0-7][0123456789abcdefghjkmnpqrstvwxyz]{25}$/;
const PREFIX_PATTERN = /^[a-z]([a-z_]{0,61}[a-z])?$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** The error every function here throws, so a caller can tell a malformed id from a bug. */
export class TypeIdError extends Error {
    name = 'TypeIdError';
}
const fail = (message) => {
    throw new TypeIdError(message);
};
/** The pattern a TypeID with this prefix must match. Used to build the Zod schema for each prefix. */
export function typeIdPattern(prefix) {
    assertPrefix(prefix);
    return new RegExp(`^${prefix}_[0-7][${ALPHABET}]{25}$`);
}
export function assertPrefix(prefix) {
    if (!PREFIX_PATTERN.test(prefix)) {
        fail(`"${prefix}" is not a TypeID prefix: 1–63 lower-case letters and inner underscores.`);
    }
}
export function isTypeId(value, prefix) {
    if (typeof value !== 'string')
        return false;
    const separator = value.lastIndexOf('_');
    if (separator <= 0)
        return false;
    const actualPrefix = value.slice(0, separator);
    const suffix = value.slice(separator + 1);
    if (!PREFIX_PATTERN.test(actualPrefix) || !SUFFIX_PATTERN.test(suffix))
        return false;
    return prefix === undefined || actualPrefix === prefix;
}
/** Is this a UUID as we store it — lower case and hyphenated? */
export function isUuid(value) {
    return typeof value === 'string' && UUID_PATTERN.test(value);
}
/**
 * Storage → wire. `uuidToTypeId('ses', '01997f5e-…')` is `ses_01j9…`.
 *
 * The UUID must already be lower case and hyphenated, which is how Postgres renders `uuid`; an
 * upper-case or braced one is a sign that something normalised it on the way, so it is rejected
 * rather than quietly accepted.
 */
export function uuidToTypeId(prefix, uuid) {
    assertPrefix(prefix);
    if (!UUID_PATTERN.test(uuid))
        fail(`"${uuid}" is not a lower-case hyphenated UUID.`);
    return `${prefix}_${encodeSuffix(uuidToBytes(uuid))}`;
}
/**
 * Wire → storage. `typeIdToUuid('ses', 'ses_01j9…')` is the `uuid` the row is keyed by.
 *
 * Pass the prefix you expect: an id of the wrong kind is the mistake this is here to catch, and it
 * is far cheaper to catch it at the edge than as a row that does not exist three layers down.
 */
export function typeIdToUuid(prefix, id) {
    assertPrefix(prefix);
    const separator = id.lastIndexOf('_');
    const actualPrefix = separator > 0 ? id.slice(0, separator) : '';
    if (actualPrefix !== prefix)
        fail(`Expected a "${prefix}_" id, got "${id}".`);
    const suffix = id.slice(separator + 1);
    if (!SUFFIX_PATTERN.test(suffix))
        fail(`"${id}" does not carry a 26-character base32 UUID.`);
    return bytesToUuid(decodeSuffix(suffix));
}
/** The prefix of a TypeID, without validating which kind it is. */
export function typeIdPrefix(id) {
    const separator = id.lastIndexOf('_');
    if (separator <= 0)
        fail(`"${id}" has no TypeID prefix.`);
    const prefix = id.slice(0, separator);
    assertPrefix(prefix);
    return prefix;
}
function uuidToBytes(uuid) {
    const hex = uuid.replaceAll('-', '');
    const bytes = new Uint8Array(16);
    for (let i = 0; i < 16; i += 1)
        bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    return bytes;
}
function bytesToUuid(bytes) {
    let hex = '';
    for (const byte of bytes)
        hex += byte.toString(16).padStart(2, '0');
    const uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    return uuid;
}
/**
 * 16 bytes (128 bits) into 26 base32 characters (130 bits). The stream is padded with two leading
 * zero bits, which is why the first character is always `0`–`7`.
 */
function encodeSuffix(bytes) {
    let accumulator = 0;
    let bits = 2;
    let out = '';
    for (const byte of bytes) {
        accumulator = (accumulator << 8) | byte;
        bits += 8;
        while (bits >= 5) {
            bits -= 5;
            out += ALPHABET[(accumulator >>> bits) & 31];
            accumulator &= (1 << bits) - 1;
        }
    }
    return out;
}
/** The inverse. The caller has already checked the shape, so the two pad bits are known to be zero. */
function decodeSuffix(suffix) {
    const bytes = new Uint8Array(16);
    let accumulator = ALPHABET.indexOf(suffix[0]) & 0b111;
    let bits = 3;
    let index = 0;
    for (let i = 1; i < SUFFIX_LENGTH; i += 1) {
        accumulator = (accumulator << 5) | ALPHABET.indexOf(suffix[i]);
        bits += 5;
        while (bits >= 8) {
            bits -= 8;
            bytes[index] = (accumulator >>> bits) & 0xff;
            index += 1;
            accumulator &= (1 << bits) - 1;
        }
    }
    return bytes;
}
//# sourceMappingURL=typeid.js.map